import React, { useState, useEffect, useRef, useCallback } from "react";
import { Card } from "../ui/Card";
import { Button } from "../ui/Button";
import type {
  CameraConfig,
  CameraSlotConfig,
} from "../../services/StorageService";

interface CameraDevice {
  deviceId: string;
  label: string;
}

interface CameraConfigPanelProps {
  cameraConfig: CameraConfig;
  onSave: (config: CameraConfig) => void;
  onCancel: () => void;
}

export const CameraConfigPanel: React.FC<CameraConfigPanelProps> = ({
  cameraConfig,
  onSave,
  onCancel,
}) => {
  const [availableDevices, setAvailableDevices] = useState<CameraDevice[]>([]);
  const [localConfig, setLocalConfig] = useState<CameraConfig>(cameraConfig);
  const [isLoading, setIsLoading] = useState(true);

  // Video refs for previews (visible)
  const slot1VideoRef = useRef<HTMLVideoElement | null>(null);
  const slot2VideoRef = useRef<HTMLVideoElement | null>(null);
  const slot3VideoRef = useRef<HTMLVideoElement | null>(null);

  // Track streams in a ref to avoid dependency issues
  const previewStreamsRef = useRef<Record<string, MediaStream>>({});

  // Enhancement processing state
  const deviceToSlotConfigRef = useRef<Record<string, CameraSlotConfig>>({});
  const processingCanvasesRef = useRef<Record<string, HTMLCanvasElement | null>>({});
  const hiddenVideosRef = useRef<Record<string, HTMLVideoElement | null>>({});
  const enhancedStreamsRef = useRef<Record<string, MediaStream | null>>({});
  const processingFrameIdsRef = useRef<Record<string, number | null>>({});

  /**
   * Apply image enhancement with slot-specific settings
   */
  const enhanceImageForDisplay = useCallback((
    ctx: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement,
    slotConfig: CameraSlotConfig
  ): void => {
    try {
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      // Use slot-specific enhancement settings
      if (!slotConfig.enhancementEnabled) return;

      const contrast = slotConfig.contrast || 1;
      const brightness = slotConfig.brightness || 0;

      // Apply contrast and brightness adjustment
      for (let i = 0; i < data.length; i += 4) {
        data[i] = Math.min(255, Math.max(0, (data[i] - 128) * contrast + 128 + brightness));
        data[i + 1] = Math.min(255, Math.max(0, (data[i + 1] - 128) * contrast + 128 + brightness));
        data[i + 2] = Math.min(255, Math.max(0, (data[i + 2] - 128) * contrast + 128 + brightness));
      }

      // Apply zoom
      const zoomFactor = slotConfig.zoom || 1;
      if (zoomFactor !== 1 && canvas.width > 0 && canvas.height > 0) {
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = canvas.width;
        tempCanvas.height = canvas.height;
        const tempCtx = tempCanvas.getContext('2d');
        if (tempCtx) {
          tempCtx.putImageData(imageData, 0, 0);

          const srcW = canvas.width / zoomFactor;
          const srcH = canvas.height / zoomFactor;
          const srcX = (canvas.width - srcW) / 2;
          const srcY = (canvas.height - srcH) / 2;

          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(tempCanvas, srcX, srcY, srcW, srcH, 0, 0, canvas.width, canvas.height);
          return;
        }
      }

      ctx.putImageData(imageData, 0, 0);
    } catch (error) {
      console.error("Error enhancing image for display:", error);
    }
  }, []);

  /**
   * Process frames from the original stream and apply enhancement
   */
  const processFrames = useCallback((deviceId: string) => {
    const hiddenVideo = hiddenVideosRef.current[deviceId];
    const canvas = processingCanvasesRef.current[deviceId];
    const slotConfig = deviceToSlotConfigRef.current[deviceId];

    if (!hiddenVideo || !canvas || !slotConfig || !slotConfig.enhancementEnabled) {
      return;
    }

    if (hiddenVideo.readyState >= 2 && hiddenVideo.videoWidth > 0 && hiddenVideo.videoHeight > 0) {
      const ctx = canvas.getContext('2d', { willReadFrequently: false, alpha: false });
      if (ctx) {
        // Draw the video frame
        ctx.drawImage(hiddenVideo, 0, 0, canvas.width, canvas.height);

        // Apply enhancement with slot-specific settings
        enhanceImageForDisplay(ctx, canvas, slotConfig);
      }
    }

    // Continue processing
    processingFrameIdsRef.current[deviceId] = requestAnimationFrame(() => processFrames(deviceId));
  }, [enhanceImageForDisplay]);

  /**
   * Start enhancement processing for a stream
   */
  const startEnhancementProcessing = useCallback((
    deviceId: string,
    stream: MediaStream,
    videoRef: React.RefObject<HTMLVideoElement | null>,
    slotConfig: CameraSlotConfig
  ) => {
    // Store the slot config for this device
    deviceToSlotConfigRef.current[deviceId] = slotConfig;

    // Stop any existing processing
    if (processingFrameIdsRef.current[deviceId] !== null && processingFrameIdsRef.current[deviceId] !== undefined) {
      cancelAnimationFrame(processingFrameIdsRef.current[deviceId]!);
      processingFrameIdsRef.current[deviceId] = null;
    }

    // Create hidden video element to receive original stream
    const hiddenVideo = document.createElement('video');
    hiddenVideo.autoplay = true;
    hiddenVideo.muted = true;
    hiddenVideo.playsInline = true;
    hiddenVideo.srcObject = stream;
    hiddenVideo.play().catch(err => console.warn('Hidden video play failed:', err));
    hiddenVideosRef.current[deviceId] = hiddenVideo;

    // Wait for video metadata to create canvas
    hiddenVideo.onloadedmetadata = () => {
      // Create canvas for processing
      const canvas = document.createElement('canvas');
      canvas.width = hiddenVideo.videoWidth;
      canvas.height = hiddenVideo.videoHeight;
      processingCanvasesRef.current[deviceId] = canvas;

      // Create enhanced stream from canvas
      const enhancedStream = canvas.captureStream(30); // 30 FPS
      enhancedStreamsRef.current[deviceId] = enhancedStream;

      // Attach enhanced stream to the visible video element
      if (videoRef.current) {
        videoRef.current.srcObject = enhancedStream;
        videoRef.current.play().catch(err => console.warn('Enhanced video play failed:', err));
      }

      // Start processing frames
      processFrames(deviceId);
    };
  }, [processFrames]);

  /**
   * Stop enhancement processing for a stream
   */
  const stopEnhancementProcessing = useCallback((deviceId: string) => {
    // Stop animation frame
    if (processingFrameIdsRef.current[deviceId] !== null && processingFrameIdsRef.current[deviceId] !== undefined) {
      cancelAnimationFrame(processingFrameIdsRef.current[deviceId]!);
      processingFrameIdsRef.current[deviceId] = null;
    }

    // Clean up hidden video
    const hiddenVideo = hiddenVideosRef.current[deviceId];
    if (hiddenVideo) {
      hiddenVideo.pause();
      hiddenVideo.srcObject = null;
      hiddenVideosRef.current[deviceId] = null;
    }

    // Clean up enhanced stream
    const enhancedStream = enhancedStreamsRef.current[deviceId];
    if (enhancedStream) {
      enhancedStream.getTracks().forEach(track => track.stop());
      enhancedStreamsRef.current[deviceId] = null;
    }

    // Clean up canvas
    processingCanvasesRef.current[deviceId] = null;

    // Clean up slot config mapping
    delete deviceToSlotConfigRef.current[deviceId];
  }, []);

  // Cleanup function to stop all camera streams
  const cleanupAllStreams = useCallback(() => {
    // Stop all enhancement processing
    Object.keys(previewStreamsRef.current).forEach((deviceId) => {
      stopEnhancementProcessing(deviceId);
    });

    // Stop original streams
    Object.values(previewStreamsRef.current).forEach((stream) => {
      stream.getTracks().forEach((track) => track.stop());
    });

    previewStreamsRef.current = {};

    // Also clear video elements
    if (slot1VideoRef.current) {
      slot1VideoRef.current.srcObject = null;
    }
    if (slot2VideoRef.current) {
      slot2VideoRef.current.srcObject = null;
    }
    if (slot3VideoRef.current) {
      slot3VideoRef.current.srcObject = null;
    }
  }, [stopEnhancementProcessing]);

  // Enumerate available cameras
  useEffect(() => {
    const enumerateDevices = async () => {
      try {
        // Request permission first
        const tempStream = await navigator.mediaDevices.getUserMedia({
          video: true,
        });
        // Stop the temp stream immediately
        tempStream.getTracks().forEach((track) => track.stop());

        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices
          .filter((device) => device.kind === "videoinput")
          .map((device) => ({
            deviceId: device?.deviceId,
            label: device?.label || `Camera ${device?.deviceId.slice(0, 8)}...`,
          }));

        setAvailableDevices(videoDevices);
      } catch (error) {
        console.error("Error enumerating devices:", error);
        setAvailableDevices([]);
      } finally {
        setIsLoading(false);
      }
    };

    enumerateDevices();

    // Cleanup streams on unmount
    return () => {
      cleanupAllStreams();
    };
  }, [cleanupAllStreams]);

  // Start camera preview
  const startCameraPreview = useCallback(async (
    deviceId: string,
    videoRef: React.RefObject<HTMLVideoElement | null>,
    slotConfig: CameraSlotConfig
  ) => {
    try {
      // Stop existing stream for this device if any
      if (previewStreamsRef.current[deviceId]) {
        stopEnhancementProcessing(deviceId);
        previewStreamsRef.current[deviceId].getTracks().forEach((track) => track.stop());
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: deviceId } },
      });

      // Store the stream in ref
      previewStreamsRef.current[deviceId] = stream;

      // If enhancement is enabled for this slot, start processing pipeline
      if (slotConfig.enhancementEnabled) {
        startEnhancementProcessing(deviceId, stream, videoRef, slotConfig);
      } else {
        // Otherwise, attach stream directly
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
      }
    } catch (error) {
      console.error("Error starting camera preview:", error);
    }
  }, [stopEnhancementProcessing, startEnhancementProcessing]);

  // Stop camera preview
  const stopCameraPreview = useCallback((deviceId: string) => {
    // Stop enhancement processing
    stopEnhancementProcessing(deviceId);

    // Stop the original stream
    if (previewStreamsRef.current[deviceId]) {
      previewStreamsRef.current[deviceId].getTracks().forEach((track) => track.stop());
      delete previewStreamsRef.current[deviceId];
    }
  }, [stopEnhancementProcessing]);

  const handleSlotChange = (slotKey: keyof CameraConfig, deviceId: string) => {
    const selectedDevice = availableDevices.find(
      (d) => d?.deviceId === deviceId
    );

    // Stop preview for previous device
    if (localConfig && localConfig[slotKey]) {
      const oldDeviceId = (localConfig[slotKey] as CameraSlotConfig)?.deviceId;
      if (oldDeviceId) {
        stopCameraPreview(oldDeviceId);
      }
    }

    // Update config while preserving enhancement settings
    const currentSlot = localConfig[slotKey] as CameraSlotConfig;
    const newSlotConfig: CameraSlotConfig = {
      deviceId: deviceId || null,
      deviceName: selectedDevice?.label || null,
      enhancementEnabled: currentSlot?.enhancementEnabled ?? false,
      contrast: currentSlot?.contrast ?? 1,
      brightness: currentSlot?.brightness ?? 0,
      zoom: currentSlot?.zoom ?? 1,
    };

    setLocalConfig((prev) => ({
      ...prev,
      [slotKey]: newSlotConfig,
    }));

    // Start preview for new device
    if (deviceId) {
      const videoRef =
        slotKey === "slot1"
          ? slot1VideoRef
          : slotKey === "slot2"
            ? slot2VideoRef
            : slot3VideoRef;
      startCameraPreview(deviceId, videoRef, newSlotConfig);
    }
  };

  // Track if we've initialized to prevent re-initialization
  const initializedRef = useRef(false);

  // Initialize previews for existing configuration
  useEffect(() => {
    if (!isLoading && availableDevices.length > 0 && !initializedRef.current) {
      initializedRef.current = true;

      if (localConfig?.slot1?.deviceId) {
        startCameraPreview(localConfig?.slot1?.deviceId, slot1VideoRef, localConfig.slot1);
      }
      if (localConfig?.slot2?.deviceId) {
        startCameraPreview(localConfig?.slot2?.deviceId, slot2VideoRef, localConfig.slot2);
      }
      if (localConfig?.slot3?.deviceId) {
        startCameraPreview(localConfig?.slot3?.deviceId, slot3VideoRef, localConfig.slot3);
      }
    }
    // Only run this effect when loading state or available devices change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, availableDevices]);

  /**
   * Re-apply enhancement when settings change
   */
  useEffect(() => {
    // Reattach all active preview streams with updated enhancement settings
    Object.entries(previewStreamsRef.current).forEach(([deviceId, stream]) => {
      // Find the correct video ref and slot config for this device
      let videoRef: React.RefObject<HTMLVideoElement | null> | null = null;
      let slotConfig: CameraSlotConfig | null = null;

      if (localConfig?.slot1?.deviceId === deviceId) {
        videoRef = slot1VideoRef;
        slotConfig = localConfig.slot1;
      } else if (localConfig?.slot2?.deviceId === deviceId) {
        videoRef = slot2VideoRef;
        slotConfig = localConfig.slot2;
      } else if (localConfig?.slot3?.deviceId === deviceId) {
        videoRef = slot3VideoRef;
        slotConfig = localConfig.slot3;
      }

      if (videoRef && slotConfig) {
        // Update the slot config in the ref
        deviceToSlotConfigRef.current[deviceId] = slotConfig;

        // If enhancement is now enabled and not already processing, start it
        if (slotConfig.enhancementEnabled && !hiddenVideosRef.current[deviceId]) {
          startEnhancementProcessing(deviceId, stream, videoRef, slotConfig);
        }
        // If enhancement is now disabled and currently processing, stop it
        else if (!slotConfig.enhancementEnabled && hiddenVideosRef.current[deviceId]) {
          stopEnhancementProcessing(deviceId);
          // Reattach original stream directly
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.play().catch(err => console.warn('Video play failed:', err));
          }
        }
      }
    });
  }, [localConfig, startEnhancementProcessing, stopEnhancementProcessing]);

  const handleSave = () => {
    // Stop all preview streams before saving
    cleanupAllStreams();
    onSave(localConfig);
  };

  const handleCancel = () => {
    // Stop all preview streams before canceling
    cleanupAllStreams();
    onCancel();
  };

  const handleReset = () => {
    // Stop all current previews
    cleanupAllStreams();

    setLocalConfig(cameraConfig);

    // Restart previews for reset config
    setTimeout(() => {
      if (cameraConfig?.slot1?.deviceId) {
        startCameraPreview(cameraConfig?.slot1?.deviceId, slot1VideoRef, cameraConfig.slot1);
      }
      if (cameraConfig?.slot2?.deviceId) {
        startCameraPreview(cameraConfig?.slot2?.deviceId, slot2VideoRef, cameraConfig.slot2);
      }
      if (cameraConfig?.slot3?.deviceId) {
        startCameraPreview(cameraConfig?.slot3?.deviceId, slot3VideoRef, cameraConfig.slot3);
      }
    }, 100);
  };

  const handleEnhancementChange = (
    slotKey: keyof CameraConfig,
    field: keyof Pick<CameraSlotConfig, 'enhancementEnabled' | 'contrast' | 'brightness' | 'zoom'>,
    value: boolean | number
  ) => {
    setLocalConfig((prev) => {
      const currentSlot = prev[slotKey] as CameraSlotConfig;
      return {
        ...prev,
        [slotKey]: {
          deviceId: currentSlot.deviceId,
          deviceName: currentSlot.deviceName,
          enhancementEnabled: field === 'enhancementEnabled' ? (value as boolean) : currentSlot.enhancementEnabled,
          contrast: field === 'contrast' ? (value as number) : currentSlot.contrast,
          brightness: field === 'brightness' ? (value as number) : currentSlot.brightness,
          zoom: field === 'zoom' ? (value as number) : currentSlot.zoom,
        },
      };
    });
  };

  const renderSlotSelector = (
    slotKey: keyof CameraConfig,
    slotLabel: string,
    slotConfig: CameraSlotConfig,
    videoRef: React.RefObject<HTMLVideoElement | null>
  ) => (
    <div key={slotKey} className="mb-6 border border-gray-200 rounded-lg p-4">
      <label className="block text-sm font-medium text-gray-700 mb-3">
        {slotLabel}
      </label>

      <div className="grid grid-cols-2 gap-4 mb-4">
        {/* Camera Selection */}
        <div>
          <select
            value={slotConfig?.deviceId || ""}
            onChange={(e) => handleSlotChange(slotKey, e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            disabled={isLoading}
          >
            <option value="">No Camera Selected</option>
            {availableDevices.map((device) => (
              <option key={device?.deviceId} value={device?.deviceId}>
                {device?.label}
              </option>
            ))}
          </select>
          {slotConfig?.deviceName && (
            <p className="text-xs text-gray-500 mt-1">
              Current: {slotConfig?.deviceName}
            </p>
          )}
        </div>

        {/* Camera Preview */}
        <div className="relative">
          <div className="w-full h-32 bg-gray-100 rounded-md border overflow-hidden">
            {slotConfig?.deviceId ? (
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                autoPlay
                muted
                playsInline
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
                No Camera Selected
              </div>
            )}
          </div>
          {slotConfig?.deviceId && (
            <div className="absolute top-1 left-1 bg-black bg-opacity-50 text-white text-xs px-1 rounded">
              Live Preview
            </div>
          )}
        </div>
      </div>

      {/* Enhancement Settings */}
      <div className="border-t border-gray-200 pt-4">
        <div className="flex items-center mb-3">
          <input
            type="checkbox"
            id={`${slotKey}-enhancement`}
            checked={slotConfig?.enhancementEnabled || false}
            onChange={(e) => handleEnhancementChange(slotKey, 'enhancementEnabled', e.target.checked)}
            className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor={`${slotKey}-enhancement`} className="text-sm font-medium text-gray-700">
            Enable Image Enhancement
          </label>
        </div>

        {slotConfig?.enhancementEnabled && (
          <div className="space-y-1.5 ml-6">
            {/* Zoom */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Zoom: {slotConfig?.zoom?.toFixed(1) || '1.0'}x
              </label>
              <input
                type="range"
                min="1"
                max="5"
                step="0.1"
                value={slotConfig?.zoom || 1}
                onChange={(e) => handleEnhancementChange(slotKey, 'zoom', parseFloat(e.target.value))}
                className="w-full h-1.5 bg-gray-300 rounded-lg appearance-none cursor-pointer"
              />
            </div>

            {/* Contrast */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Contrast: {slotConfig?.contrast?.toFixed(1) || '1.0'}
              </label>
              <input
                type="range"
                min="0.5"
                max="5"
                step="0.1"
                value={slotConfig?.contrast || 1}
                onChange={(e) => handleEnhancementChange(slotKey, 'contrast', parseFloat(e.target.value))}
                className="w-full h-1.5 bg-gray-300 rounded-lg appearance-none cursor-pointer"
              />
            </div>

            {/* Brightness */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Brightness: {slotConfig?.brightness || 0}
              </label>
              <input
                type="range"
                min="-200"
                max="200"
                step="5"
                value={slotConfig?.brightness || 0}
                onChange={(e) => handleEnhancementChange(slotKey, 'brightness', parseInt(e.target.value))}
                className="w-full h-1.5 bg-gray-300 rounded-lg appearance-none cursor-pointer"
              />
            </div>

          </div>
        )}
      </div>
    </div>
  );

  return (
    <Card
      padding="lg"
      className="max-w-4xl mx-auto max-h-[90vh] overflow-y-auto"
    >
      <h2 className="text-xl font-semibold mb-6 text-gray-800">
        Camera Configuration
      </h2>

      {isLoading ? (
        <div className="text-center py-8">
          <p className="text-gray-500">Loading available cameras...</p>
        </div>
      ) : availableDevices.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-red-500 mb-2">No cameras detected</p>
          <p className="text-sm text-gray-500">
            Please ensure cameras are connected and permission is granted.
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-6 mb-6">
            {renderSlotSelector(
              "slot1",
              "Camera Slot 1",
              localConfig?.slot1,
              slot1VideoRef
            )}
            {renderSlotSelector(
              "slot2",
              "Camera Slot 2",
              localConfig?.slot2,
              slot2VideoRef
            )}
            {renderSlotSelector(
              "slot3",
              "Camera Slot 3",
              localConfig?.slot3,
              slot3VideoRef
            )}
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
            <h3 className="text-sm font-medium text-blue-800 mb-2">
              Configuration Notes:
            </h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• The same camera can be assigned to multiple slots</li>
              <li>• Slots can be left unassigned if not needed</li>
              <li>• Live previews show the actual camera feed</li>
              <li>• Changes will apply to the Multi-Camera Operator View</li>
            </ul>
          </div>

          <div className="flex justify-end space-x-3">
            <Button variant="secondary" onClick={handleReset}>
              Reset
            </Button>
            <Button variant="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave}>
              Save Configuration
            </Button>
          </div>
        </>
      )}
    </Card>
  );
};
